import { BsdsInput } from '@bsds-lib-component/react';
import React, { useId, type MutableRefObject } from 'react';

import { CpfInput, Dropdown, Visibility } from '../..';
import { usePersonFormSection } from '../../../hooks/vida/usePersonFormSection';
import type { ProponentFormValues } from '../../../types/vida/cadastralMaintenance/proponentFormTypes';
import type { PersonFormSectionLabels, PersonFormSection, PersonFormSectionControl } from '../../../types/vida/cadastralMaintenance/personFormTypes';
import {
  CARGO_FUNCAO_MAX_LENGTH,
  COMPLEMENTO_MAX_LENGTH,
  MATRICULA_MAX_LENGTH,
  NUMERO_MAX_LENGTH,
  PERSON_NAME_MAX_LENGTH,
} from '../../../utils/insuredFormUtils';
import {
  getProponentFormValuesFromInitial,
  isProponentFormValid,
  PROPONENT_ESTADO_CIVIL_OPTIONS,
  PROPONENT_SEX_OPTIONS,
} from '../../../utils/proponentFormUtils';
import {
  PersonFormAddressFields,
  PersonFormDateInput,
  PersonFormInsuranceFields,
  PersonFormSectionNav,
} from './PersonFormSharedFields';

type SharedProponentFormSectionLabels = PersonFormSectionLabels;

type SharedProponentFormSectionProps = {
  labels: SharedProponentFormSectionLabels;
  onValidityChange?: (isValid: boolean) => void;
  onValuesChange?: (values: ProponentFormValues) => void;
  initialValues?: ProponentFormValues;
  readOnly?: boolean;
  extraNavButtons?: React.ReactNode;
  hideFormSections?: boolean;
  onSectionNavChange?: (section: PersonFormSection) => void;
  sectionControlRef?: MutableRefObject<PersonFormSectionControl | null>;
  onActiveSectionChange?: (section: PersonFormSection) => void;
};

const SharedProponentFormSection: React.FC<SharedProponentFormSectionProps> = ({
  labels,
  onValidityChange,
  onValuesChange,
  initialValues,
  readOnly = false,
  extraNavButtons,
  hideFormSections = false,
  onSectionNavChange,
  sectionControlRef,
  onActiveSectionChange,
}) => {
  const sexoDropdownId = useId();
  const estadoCivilDropdownId = useId();
  const ufDropdownId = useId();
  const {
    values,
    activeSection,
    setActiveSection,
    setIsCpfValid,
    handleInputChange,
    handleCepChange,
    handleCpfChange,
    handleDropdownChange,
    handleZipCodeSearch,
    isZipCodeLookupLoading,
    zipCodeLookupError,
    inputDisabled,
  } = usePersonFormSection({
    initialValues,
    getFormValuesFromInitial: getProponentFormValuesFromInitial,
    isBaseFormValid: isProponentFormValid,
    readOnly,
    onValidityChange,
    onValuesChange,
    sectionControlRef,
    onActiveSectionChange,
  });

  const handleSectionChange = (section: PersonFormSection) => {
    setActiveSection(section);
    onSectionNavChange?.(section);
  };

  return (
    <>
      <PersonFormSectionNav
        labels={labels}
        activeSection={activeSection}
        onSectionChange={handleSectionChange}
        extraNavButtons={extraNavButtons}
        highlightActiveSection={!hideFormSections}
      />

      {!hideFormSections && (
        <>
          <Visibility isActive={activeSection === 'personData'} animated animationDuration={300}>
            <div>
              <div className="bsds-mt-16">
                <div className="grid grid-cols-1 gap-4">
                  <BsdsInput
                    label="Nome Completo *"
                    value={values.nome}
                    onInput={handleInputChange('nome')}
                    type="text"
                    clearInput={false}
                    platform="web"
                    required
                    disabled={inputDisabled}
                    maxlength={PERSON_NAME_MAX_LENGTH}
                  />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 bsds-mt-16">
                  <CpfInput
                    label="CPF *"
                    value={values.cpf}
                    onChange={handleCpfChange}
                    onValidationChange={setIsCpfValid}
                    required
                    disabled={inputDisabled}
                  />
                  <BsdsInput
                    label="Matrícula *"
                    value={values.matricula}
                    onInput={handleInputChange('matricula')}
                    type="text"
                    platform="web"
                    required
                    disabled={inputDisabled}
                    clearInput={false}
                    maxlength={MATRICULA_MAX_LENGTH}
                  />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 bsds-mt-16">
                  <Dropdown
                    id={sexoDropdownId}
                    label="Sexo *"
                    placeholder="Selecione o sexo"
                    value={values.sexo}
                    onChange={handleDropdownChange('sexo')}
                    options={PROPONENT_SEX_OPTIONS}
                    required
                    disabled={inputDisabled}
                  />
                  <Dropdown
                    id={estadoCivilDropdownId}
                    label="Estado Civil *"
                    placeholder="Selecione o estado civil"
                    value={values.estadoCivil}
                    onChange={handleDropdownChange('estadoCivil')}
                    options={PROPONENT_ESTADO_CIVIL_OPTIONS}
                    required
                    disabled={inputDisabled}
                  />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 bsds-mt-16">
                  <BsdsInput
                    label="Documento"
                    value={values.documento}
                    onInput={handleInputChange('documento')}
                    type="text"
                    platform="web"
                    disabled={inputDisabled}
                    clearInput={false}
                  />
                  <PersonFormDateInput
                    label="Data de Nascimento *"
                    value={values.dataNascimento}
                    onInput={handleInputChange('dataNascimento')}
                    disabled={inputDisabled}
                    ariaLabel="Data de nascimento (obrigatório)"
                  />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 bsds-mt-16">
                  <BsdsInput
                    label="Cargo/Função *"
                    value={values.cargo}
                    onInput={handleInputChange('cargo')}
                    type="text"
                    platform="web"
                    required
                    disabled={inputDisabled}
                    clearInput={false}
                    maxlength={CARGO_FUNCAO_MAX_LENGTH}
                  />
                </div>
              </div>
            </div>
          </Visibility>

          <PersonFormAddressFields
            isActive={activeSection === 'personAddress'}
            values={values}
            ufDropdownId={ufDropdownId}
            inputDisabled={inputDisabled}
            onInputChange={handleInputChange}
            onCepChange={handleCepChange}
            onDropdownChange={handleDropdownChange}
            onZipCodeSearch={() => {
              void handleZipCodeSearch();
            }}
            isZipCodeLookupLoading={isZipCodeLookupLoading}
            zipCodeLookupError={zipCodeLookupError}
            extraFields={(
              <>
                <BsdsInput
                  label="Numero *"
                  value={values.numeroLogradouro}
                  onInput={handleInputChange('numeroLogradouro')}
                  type="text"
                  inputmode="numeric"
                  platform="web"
                  required
                  disabled={inputDisabled}
                  clearInput={false}
                  maxlength={NUMERO_MAX_LENGTH}
                />
                <BsdsInput
                  label="Complemento"
                  value={values.complemento}
                  onInput={handleInputChange('complemento')}
                  type="text"
                  platform="web"
                  disabled={inputDisabled}
                  clearInput={false}
                  maxlength={COMPLEMENTO_MAX_LENGTH}
                />
              </>
            )}
          />

          <PersonFormInsuranceFields
            isActive={activeSection === 'insuranceInfo'}
            values={values}
            inputDisabled={inputDisabled}
            onInputChange={handleInputChange}
          />
        </>
      )}
    </>
  );
};

export type { SharedProponentFormSectionLabels, SharedProponentFormSectionProps };
export default SharedProponentFormSection;
